import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Vorschlag {
  vorschlag_id: number;
  typ: 'FREITEXT_MUSTER' | 'HAEUFIGE_FRAGE' | 'WARNSCHWELLE';
  status: 'OFFEN' | 'GENEHMIGT' | 'ABGELEHNT' | 'UMGESETZT';
  titel: string;
  beschreibung: string;
  begruendung?: string;
  beispiele?: string;  // JSON-String
  haeufigkeit: number;
  artefakt_code?: string;
  analysiert_am: string;
  entschieden_am?: string;
  entscheider_name?: string;
}

interface Props {
  authToken: string;
}

const TYP_LABEL: Record<string, { label: string; farbe: string; beschreibung: string }> = {
  FREITEXT_MUSTER: {
    label: 'Freitext-Muster',
    farbe: 'var(--green-900)',
    beschreibung: 'Strukturierte Daten wurden in Freitext-Feldern entdeckt',
  },
  HAEUFIGE_FRAGE: {
    label: 'Häufige Frage',
    farbe: 'var(--green-900)',
    beschreibung: 'Wiederkehrende Frage ohne dediziertes Tool',
  },
  WARNSCHWELLE: {
    label: 'Warnschwelle',
    farbe: 'var(--gold)',
    beschreibung: 'Hardcodierter Wert den Nutzer hinterfragen',
  },
};

const STATUS_LABEL: Record<string, { label: string; farbe: string }> = {
  OFFEN:     { label: 'Offen',     farbe: 'var(--ink-2)' },
  GENEHMIGT: { label: 'Genehmigt', farbe: 'var(--green-500)' },
  ABGELEHNT: { label: 'Abgelehnt', farbe: 'var(--red)' },
  UMGESETZT: { label: 'Umgesetzt', farbe: 'var(--green-900)' },
};

const MusterVorschlaege: React.FC<Props> = ({ authToken }) => {
  const [vorschlaege, setVorschlaege] = useState<Vorschlag[]>([]);
  const [loading, setLoading]         = useState(true);
  const [analyseLoading, setAnalyseLoading] = useState(false);
  const [statusFilter, setStatusFilter]     = useState<string>('OFFEN');
  const [aufgeklappt, setAufgeklappt]       = useState<Set<number>>(new Set());
  const [artefaktModal, setArtefaktModal]   = useState<{ titel: string; code: string } | null>(null);
  const [aktionLoading, setAktionLoading]   = useState<number | null>(null);
  const [meldung, setMeldung]               = useState<{ typ: 'ok' | 'err'; text: string } | null>(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status_filter: statusFilter } : {};
      const res = await axios.get(`${API_URL}/api/muster-vorschlaege`, { headers, params });
      setVorschlaege(res.data);
    } catch (e: any) {
      setMeldung({ typ: 'err', text: 'Laden fehlgeschlagen: ' + (e.response?.data?.detail || e.message) });
    } finally {
      setLoading(false);
    }
  }, [headers, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleAufgeklappt = (id: number) => {
    setAufgeklappt(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const genehmige = async (v: Vorschlag) => {
    setAktionLoading(v.vorschlag_id);
    try {
      const res = await axios.post(
        `${API_URL}/api/muster-vorschlaege/${v.vorschlag_id}/genehmigen`,
        {},
        { headers }
      );
      if (v.typ === 'WARNSCHWELLE') {
        setMeldung({ typ: 'ok', text: res.data.artefakt_code });
      } else {
        setArtefaktModal({ titel: v.titel, code: res.data.artefakt_code });
      }
      await load();
    } catch (e: any) {
      setMeldung({ typ: 'err', text: 'Genehmigung fehlgeschlagen: ' + (e.response?.data?.detail || e.message) });
    } finally {
      setAktionLoading(null);
    }
  };

  const ablehne = async (v: Vorschlag) => {
    if (!window.confirm(`Vorschlag "${v.titel}" ablehnen?`)) return;
    setAktionLoading(v.vorschlag_id);
    try {
      await axios.post(
        `${API_URL}/api/muster-vorschlaege/${v.vorschlag_id}/ablehnen`,
        {},
        { headers }
      );
      setMeldung({ typ: 'ok', text: 'Abgelehnt.' });
      await load();
    } catch (e: any) {
      setMeldung({ typ: 'err', text: 'Ablehnung fehlgeschlagen: ' + (e.response?.data?.detail || e.message) });
    } finally {
      setAktionLoading(null);
    }
  };

  const starteAnalyse = async () => {
    setAnalyseLoading(true);
    setMeldung(null);
    try {
      const res = await axios.post(`${API_URL}/api/muster-vorschlaege/analyse-starten`, {}, { headers });
      const { neu, uebersprungen, fehler } = res.data;
      if (fehler) {
        setMeldung({ typ: 'err', text: `Analyse fehlgeschlagen: ${fehler}` });
      } else {
        setMeldung({
          typ: 'ok',
          text: `Analyse abgeschlossen: ${neu} neue Vorschläge, ${uebersprungen} übersprungen.`,
        });
        if (neu > 0) await load();
      }
    } catch (e: any) {
      setMeldung({ typ: 'err', text: 'Fehler: ' + (e.response?.data?.detail || e.message) });
    } finally {
      setAnalyseLoading(false);
    }
  };

  const parseBeispiele = (json?: string): string[] => {
    if (!json) return [];
    try { return JSON.parse(json); } catch { return []; }
  };

  const offeneAnzahl = vorschlaege.filter(v => v.status === 'OFFEN').length;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>Systemvorschläge</h2>
          <p style={{ color: 'var(--ink-2)', fontSize: 14, margin: '4px 0 0' }}>
            Automatisch erkannte Muster aus Chatverläufen und Freitext-Feldern.
            Läuft jeden Sonntag um 03:00 Uhr.
          </p>
        </div>
        <button
          onClick={starteAnalyse}
          disabled={analyseLoading}
          style={{
            padding: '8px 16px',
            background: analyseLoading ? 'var(--ink-2)' : 'var(--green-900)',
            color: 'var(--bone)',
            border: 'none',
            borderRadius: 0,
            cursor: analyseLoading ? 'not-allowed' : 'pointer',
            fontSize: 13,
            whiteSpace: 'nowrap',
          }}
        >
          {analyseLoading ? 'Analysiere...' : 'Analyse jetzt starten'}
        </button>
      </div>

      {/* Meldung */}
      {meldung && (
        <div style={{
          background: meldung.typ === 'ok' ? 'var(--bone-2)' : 'var(--bone-2)',
          border: `1px solid ${meldung.typ === 'ok' ? 'var(--green-500)' : 'var(--red)'}`,
          borderRadius: 0,
          padding: '10px 14px',
          marginBottom: 16,
          fontSize: 13,
          whiteSpace: 'pre-wrap',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
        }}>
          <span>{meldung.text}</span>
          <button
            onClick={() => setMeldung(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', flexShrink: 0 }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['OFFEN', 'GENEHMIGT', 'ABGELEHNT', 'UMGESETZT', ''].map(s => (
          <button
            key={s || 'alle'}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: '5px 12px',
              borderRadius: 0,
              border: '1px solid var(--bone-3)',
              background: statusFilter === s ? 'var(--green-900)' : 'var(--bone)',
              color: statusFilter === s ? 'var(--bone)' : 'var(--ink-2)',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            {s || 'Alle'}
            {s === 'OFFEN' && offeneAnzahl > 0 && (
              <span style={{
                marginLeft: 6,
                background: statusFilter === 'OFFEN' ? 'var(--bone)' : 'var(--red)',
                color: statusFilter === 'OFFEN' ? 'var(--green-900)' : 'var(--bone)',
                borderRadius: 0,
                padding: '0 6px',
                fontSize: 11,
              }}>{offeneAnzahl}</span>
            )}
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <p style={{ color: 'var(--ink-2)', textAlign: 'center', padding: 40 }}>Laden...</p>
      ) : vorschlaege.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '48px 24px',
          background: 'var(--bone-2)',
          borderRadius: 0,
          border: '1px dashed var(--bone-3)',
        }}>
          <p style={{ color: 'var(--ink-2)', margin: 0 }}>
            {statusFilter === 'OFFEN'
              ? 'Keine offenen Vorschläge. Starte eine Analyse oder warte auf den Sonntag.'
              : 'Keine Vorschläge in diesem Status.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {vorschlaege.map(v => {
            const typInfo    = TYP_LABEL[v.typ] || { label: v.typ, farbe: 'var(--ink-2)', beschreibung: '' };
            const statusInfo = STATUS_LABEL[v.status] || { label: v.status, farbe: 'var(--ink-2)' };
            const isOpen     = aufgeklappt.has(v.vorschlag_id);
            const beispiele  = parseBeispiele(v.beispiele);
            const isBusy     = aktionLoading === v.vorschlag_id;

            return (
              <div
                key={v.vorschlag_id}
                style={{
                  border: '1px solid var(--bone-3)',
                  borderRadius: 0,
                  overflow: 'hidden',
                  background: 'var(--bone)',
                }}
              >
                {/* Karten-Header */}
                <div
                  onClick={() => toggleAufgeklappt(v.vorschlag_id)}
                  style={{
                    padding: '14px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 12,
                    background: isOpen ? 'var(--bone-2)' : 'var(--bone)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{
                        background: typInfo.farbe,
                        color: 'var(--bone)',
                        borderRadius: 0,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                      }}>{typInfo.label}</span>
                      <span style={{
                        border: `1px solid ${statusInfo.farbe}`,
                        color: statusInfo.farbe,
                        borderRadius: 0,
                        padding: '1px 7px',
                        fontSize: 11,
                      }}>{statusInfo.label}</span>
                      <span style={{ fontSize: 11, color: 'var(--ink-2)' }}>
                        {v.haeufigkeit}x erkannt
                      </span>
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>{v.titel}</div>
                    <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>{v.beschreibung}</div>
                  </div>
                  <span style={{ color: 'var(--ink-2)', fontSize: 18, flexShrink: 0 }}>
                    {isOpen ? '▲' : '▼'}
                  </span>
                </div>

                {/* Aufgeklappter Bereich */}
                {isOpen && (
                  <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--bone-2)' }}>
                    {v.begruendung && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>
                          BEGRÜNDUNG
                        </div>
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6 }}>
                          {v.begruendung}
                        </p>
                      </div>
                    )}

                    {beispiele.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>
                          BEISPIELE
                        </div>
                        <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                          {beispiele.map((b, i) => (
                            <li key={i} style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 2 }}>{b}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {v.artefakt_code && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>
                          GENERIERTER CODE
                        </div>
                        <pre style={{
                          background: 'var(--ink)',
                          color: 'var(--bone-3)',
                          padding: 12,
                          borderRadius: 0,
                          fontSize: 12,
                          overflow: 'auto',
                          maxHeight: 200,
                          margin: 0,
                        }}>{v.artefakt_code}</pre>
                      </div>
                    )}

                    <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ink-2)' }}>
                      Analysiert: {new Date(v.analysiert_am).toLocaleDateString('de-DE')}
                      {v.entscheider_name && ` · Entschieden von ${v.entscheider_name}`}
                    </div>

                    {/* Aktions-Buttons – nur für OFFEN */}
                    {v.status === 'OFFEN' && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); genehmige(v); }}
                          disabled={isBusy}
                          style={{
                            padding: '7px 16px',
                            background: isBusy ? 'var(--ink-2)' : 'var(--green-500)',
                            color: 'var(--bone)',
                            border: 'none',
                            borderRadius: 0,
                            cursor: isBusy ? 'not-allowed' : 'pointer',
                            fontSize: 13,
                          }}
                        >
                          {isBusy ? 'Bitte warten...' : 'Genehmigen'}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); ablehne(v); }}
                          disabled={isBusy}
                          style={{
                            padding: '7px 16px',
                            background: 'var(--bone)',
                            color: 'var(--red)',
                            border: '1px solid var(--red)',
                            borderRadius: 0,
                            cursor: isBusy ? 'not-allowed' : 'pointer',
                            fontSize: 13,
                          }}
                        >
                          Ablehnen
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Artefakt-Modal (für FREITEXT_MUSTER und HAEUFIGE_FRAGE) */}
      {artefaktModal && (
        <div
          onClick={() => setArtefaktModal(null)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bone)',
              borderRadius: 0,
              padding: 24,
              maxWidth: 680,
              width: '95%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Generierter Code – {artefaktModal.titel}</h3>
              <button
                onClick={() => setArtefaktModal(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--ink-2)' }}
              >✕</button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
              Führe diesen Code manuell aus. Er wurde nicht automatisch angewendet.
            </p>
            <pre style={{
              background: 'var(--ink)',
              color: 'var(--bone-3)',
              padding: 16,
              borderRadius: 0,
              fontSize: 12,
              overflow: 'auto',
              margin: 0,
              whiteSpace: 'pre-wrap',
            }}>{artefaktModal.code}</pre>
            <button
              onClick={() => {
                navigator.clipboard.writeText(artefaktModal.code);
                setMeldung({ typ: 'ok', text: 'Code in Zwischenablage kopiert.' });
                setArtefaktModal(null);
              }}
              style={{
                marginTop: 14,
                padding: '8px 18px',
                background: 'var(--green-900)',
                color: 'var(--bone)',
                border: 'none',
                borderRadius: 0,
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Code kopieren
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusterVorschlaege;
